import { useContext, useState } from "react";
import { flushSync } from "react-dom";
import {
  matchRoutes,
  resolvePath,
  UNSAFE_DataRouterContext,
  useLocation,
  useViewTransitionState,
  type DataRouter,
  type RouteObject,
  type RouterNavigateOptions,
  type To,
} from "react-router-dom";

/**
 * Motion with a purpose (design 2026-09-27 §4.9; idea 26 of the UI research,
 * 18.10.2026; „Stahl", design 2026-10-24 §7).
 *
 * Two changes are shown rather than jumped: a page turn inside the shell and a
 * change of step on the planning board, both through the View Transitions
 * API. Duration and curve are the motion tokens (`base.css`), not numbers of
 * this module's.
 *
 * A page turn moves a few things under names of their own — allowed since
 * „Stahl" (§8.7): the masthead stands (`kopf`), the place's mark glides to the
 * new one (`ort-marke`), the Vorgang's head stands from tab to tab
 * (`vorgang-kopf`) with the words of its tabs (`reiter`) and the red pill
 * gliding under them (`reiter-marke`), the Vorgang's name — number and title
 * as one piece — flies from the list into the record (`vorgang-name`), and
 * the page under the Vorgang's frame rises in — or, from a tab, turns in
 * reading direction (`seite-a`/`seite-b`, `usePageSide`, `setTurnDirection`).
 *
 * A step change on the board is not a page turn: it stays one cross-fade of
 * the whole page. It marks itself (`data-vt="schritt"`, as the theme reveal
 * marks itself `"theme"`), and under that mark base.css switches every name
 * off — nothing slides or grows under a pointer at work on the board.
 *
 * The motion is an addition, never a condition: without view transitions
 * (Firefox before 144, jsdom) or under reduced motion the change happens at
 * once, exactly as it did before.
 */

/** Whether the person asked the system for less motion. jsdom has no
 * `matchMedia`; a test render must not throw, and the answer there is „no". */
export function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/** Whether a cross-fade may run: the browser has view transitions, and the
 * person has not asked for less motion. */
function mayCrossFade(): boolean {
  return typeof document.startViewTransition === "function" && !prefersReducedMotion();
}

/** The marked transition under way: whichever started last owns `data-vt`. */
let marked: ViewTransition | null = null;

/**
 * Runs `update` as a view transition marked `kind` on `<html data-vt>`: the
 * mark goes on before the browser takes its picture of the old page — the
 * pictures are what base.css styles by it — and comes off once the
 * transition is over, unless a newer marked one has taken it over (a step
 * change cutting a theme reveal short, or the other way round). `flushSync`:
 * React draws the change inside the transition, not a frame after it.
 * Callers check first that a transition may run (`mayCrossFade`).
 */
export function startMarkedTransition(kind: "schritt" | "theme", update: () => void): void {
  const root = document.documentElement;
  const before = root.dataset.vt;
  root.dataset.vt = kind;
  let transition: ViewTransition;
  try {
    transition = document.startViewTransition(() => flushSync(update));
  } catch {
    // A browser that throws instead of starting (final review, M-5): the mark
    // goes back to what it was — left standing, it would switch every name
    // of the next page turn off — and the change happens at once, as it
    // would without view transitions (the theme is applied all the same).
    if (before === undefined) delete root.dataset.vt;
    else root.dataset.vt = before;
    update();
    return;
  }
  marked = transition;
  // A second change inside the transition skips this one: the browser
  // rejects `ready` with an AbortError and runs `update` all the same. That
  // is the transition giving way, not a failure, and it must not reach the
  // console as „Uncaught (in promise)". An `update` that throws still shows
  // there — through `updateCallbackDone` and `finished`, which stay unhandled.
  transition.ready.catch(() => undefined);
  const done = (): void => {
    if (marked !== transition) return;
    marked = null;
    delete root.dataset.vt;
  };
  transition.finished.then(done, done);
}

/**
 * Makes a change of state on a page — a step on the board — as a cross-fade
 * of the whole page where it may run, at once where it may not. The browser
 * takes its picture of the old page first and runs `update` after, so the
 * change lands a frame later than it would without.
 */
export function withViewTransition(update: () => void): void {
  if (!mayCrossFade()) {
    update();
    return;
  }
  startMarkedTransition("schritt", update);
}

/**
 * Which way the next page turn goes, as a tab of the Vorgang says it (design
 * 2026-10-24 §7): `"vor"` to a tab right of the lit one — the old page leaves
 * to the left, the new one comes from the right —, `"zurueck"` to one left of
 * it, `null` for no direction (the page rises in). `"flug"` is the list's
 * row opening its Vorgang (UX-Gutachten 2, B-3): the page rises in once the
 * Vorgang's name has flown past where it comes in. On `<html
 * data-vt-richtung>`, where base.css reads it; nothing else does, so set and
 * left standing it is harmless where no transition runs — and the frame
 * takes it away once the page has turned (`CaseFrame`).
 */
export function setTurnDirection(direction: "vor" | "zurueck" | "flug" | null): void {
  const root = document.documentElement;
  if (direction === null) delete root.dataset.vtRichtung;
  else root.dataset.vtRichtung = direction;
}

/**
 * Whether React Router is turning the page to or from `to` right now —
 * its `useViewTransitionState`, which knows the turn from the moment the
 * router draws the old page for the browser's picture until the browser has
 * played it. Only a data router can say: under a plain `MemoryRouter` (the
 * screen tests) the router's own hook throws, and the answer there is „no".
 */
export function usePageTurn(to: string): boolean {
  const inDataRouter = useContext(UNSAFE_DataRouterContext) !== null;
  // Conditional on purpose, and stable: a component never moves between a
  // data router and none while it is mounted, so the hooks run in the same
  // order on every render of it.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return inDataRouter ? useViewTransitionState(to) : false;
}

/**
 * The handle of the route that draws the shell — masthead and work area.
 * A page turn between two pages under it cross-fades; to or from a page
 * outside it (the sign-in door, the print sheets) it does not: those have no
 * masthead that stands, and the door has its own entrance.
 */
export const SHELL_ROUTE = { shell: true } as const;

function isShell(handle: unknown): boolean {
  return (handle as { shell?: unknown } | null | undefined)?.shell === true;
}

/** Whether `pathname` is a page under the shell. */
export function insideShell(routes: RouteObject[], pathname: string): boolean {
  return (matchRoutes(routes, pathname) ?? []).some((match) => isShell(match.route.handle));
}

/**
 * Gives every navigation of `router` a default for React Router's own
 * `viewTransition` option: on when the page turns inside the shell and a
 * cross-fade may run, off otherwise. A caller that says it itself keeps its
 * word. A change of the query or the hash is not a page turn — filters, tabs
 * and the record's own parameters change there while somebody works, and
 * that is no moment for a fade.
 *
 * One default here instead of the option on sixty links and calls: a link
 * written tomorrow fades like the ones written today. React Router still
 * starts the transition itself, and only where `document.startViewTransition`
 * exists; it also fades the way back over the browser's own buttons.
 */
export function crossFadePageTurns(router: DataRouter): DataRouter {
  const navigate = router.navigate.bind(router);

  function pageTurn(to: To | null): boolean {
    const from = router.state.location.pathname;
    const next = to === null ? from : resolvePath(to, from).pathname;
    return next !== from && insideShell(router.routes, from) && insideShell(router.routes, next);
  }

  function crossFading(to: number): Promise<void>;
  function crossFading(to: To | null, opts?: RouterNavigateOptions): Promise<void>;
  function crossFading(to: number | To | null, opts?: RouterNavigateOptions): Promise<void> {
    if (typeof to === "number") return navigate(to);
    return navigate(to, { ...opts, viewTransition: opts?.viewTransition ?? (mayCrossFade() && pageTurn(to)) });
  }

  router.navigate = crossFading;
  return router;
}

/**
 * Which of its two names the page under the Vorgang's frame carries:
 * `seite-a` or `seite-b`, the other one after every navigation (design
 * 2026-10-24 §7). The old page and the new one are then two groups of a page
 * turn, each where it stands — the old one leaves from its own place, the
 * new one comes in at its own. Under one name they were one group, standing
 * where the new page lies, and the old page jumped there first: down by the
 * frame's height from the list into a Vorgang, by its name's line from the
 * record onto a spoke. Read for the shell's page block (`AppShell`).
 */
export function usePageSide(): "a" | "b" {
  const { key } = useLocation();
  const [shown, setShown] = useState<{ key: string; side: "a" | "b" }>({ key, side: "a" });
  if (shown.key === key) return shown.side;
  // Adjusted while rendering, as React has it for state that follows a
  // prop: the new page is drawn with its new name in the same pass.
  const side = shown.side === "a" ? "b" : "a";
  setShown({ key, side });
  return side;
}
